import { createSlice, createListenerMiddleware } from '@reduxjs/toolkit'

const STORAGE_KEYS = {
  apiKey: 'webui-api-key',
  writeKey: 'webui-write-key',
}

const LEGACY_STORAGE_KEYS = {
  apiKeys: 'activity-stream-api-keys',
  activeKeyId: 'activity-stream-active-key-id',
  legacySingleKey: 'activity-stream-api-key',
}

const inMemorySessionStorage = new Map()

const getSessionStorage = () => {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return {
      getItem: (key) => inMemorySessionStorage.get(key) ?? null,
      setItem: (key, value) => {
        inMemorySessionStorage.set(key, value)
      },
      removeItem: (key) => {
        inMemorySessionStorage.delete(key)
      },
    }
  }
  return window.sessionStorage
}

const getLocalStorage = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return {
      getItem: () => null,
      setItem: () => {},
      removeItem: () => {},
    }
  }
  return window.localStorage
}

const readSessionCredentials = () => {
  const storage = getSessionStorage()
  const apiKey = storage.getItem(STORAGE_KEYS.apiKey)
  const writeKey = storage.getItem(STORAGE_KEYS.writeKey)

  return {
    apiKey: apiKey ?? '',
    writeKey: writeKey ?? '',
  }
}

const readLegacyCredentials = () => {
  try {
    const storage = getLocalStorage()
    const storedKeys = storage.getItem(LEGACY_STORAGE_KEYS.apiKeys)
    const activeKeyId = storage.getItem(LEGACY_STORAGE_KEYS.activeKeyId)

    if (storedKeys) {
      const parsed = JSON.parse(storedKeys)
      if (Array.isArray(parsed) && parsed.length > 0) {
        if (activeKeyId) {
          const active = parsed.find((key) => key.id === activeKeyId)
          if (active?.key) {
            return { apiKey: active.key, writeKey: '' }
          }
        }
        const firstKey = parsed[0]
        if (firstKey?.key) {
          return { apiKey: firstKey.key, writeKey: '' }
        }
      }
    }

    const legacySingleKey = storage.getItem(LEGACY_STORAGE_KEYS.legacySingleKey)
    if (legacySingleKey) {
      return { apiKey: legacySingleKey, writeKey: '' }
    }
  } catch (_error) {
    // ignore legacy read failures
  }

  return { apiKey: '', writeKey: '' }
}

const sessionCredentials = readSessionCredentials()
const legacyCredentials = !sessionCredentials.apiKey ? readLegacyCredentials() : { apiKey: '', writeKey: '' }
const mergedCredentials = {
  apiKey: sessionCredentials.apiKey || legacyCredentials.apiKey,
  writeKey: sessionCredentials.writeKey || legacyCredentials.writeKey,
}

const initialState = {
  apiKey: mergedCredentials.apiKey,
  writeKey: mergedCredentials.writeKey,
  isAuthenticated: Boolean(mergedCredentials.apiKey),
  lastSyncedAt: mergedCredentials.apiKey || mergedCredentials.writeKey ? Date.now() : null,
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (state, action) => {
      const trimmedApiKey = action.payload?.apiKey?.trim() ?? ''
      const trimmedWriteKey = action.payload?.writeKey?.trim() ?? ''

      state.apiKey = trimmedApiKey
      state.writeKey = trimmedWriteKey
      state.isAuthenticated = Boolean(trimmedApiKey)
      state.lastSyncedAt = Date.now()
    },
    clearCredentials: (state) => {
      state.apiKey = ''
      state.writeKey = ''
      state.isAuthenticated = false
      state.lastSyncedAt = Date.now()
    },
  },
})

export const { setCredentials, clearCredentials } = authSlice.actions

const syncSessionStorage = (apiKey, writeKey) => {
  const storage = getSessionStorage()
  if (apiKey) {
    storage.setItem(STORAGE_KEYS.apiKey, apiKey)
  } else {
    storage.removeItem(STORAGE_KEYS.apiKey)
  }

  if (writeKey) {
    storage.setItem(STORAGE_KEYS.writeKey, writeKey)
  } else {
    storage.removeItem(STORAGE_KEYS.writeKey)
  }
}

const authListenerMiddleware = createListenerMiddleware()

authListenerMiddleware.startListening({
  actionCreator: setCredentials,
  effect: async (_, listenerApi) => {
    const state = listenerApi.getState().auth
    syncSessionStorage(state.apiKey, state.writeKey)
  },
})

authListenerMiddleware.startListening({
  actionCreator: clearCredentials,
  effect: async () => {
    syncSessionStorage('', '')
  },
})

export const selectAuthState = (state) => state.auth
export const selectApiKey = (state) => state.auth.apiKey
export const selectWriteKey = (state) => state.auth.writeKey
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated

export { STORAGE_KEYS, authListenerMiddleware, initialState as authInitialState }

export const readLegacyCredentialsSnapshot = () => readLegacyCredentials()

export default authSlice.reducer
